import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Scale, Table, MessageSquareText, Download, Upload, CheckCircle2, XCircle } from 'lucide-react'
import { exportBundle, importBundle, getRequirements } from '../services/gutachtenApi'

interface TileProps {
  to: string
  icon: React.ReactNode
  title: string
  description: string
}

function Tile({ to, icon, title, description }: TileProps) {
  return (
    <Link
      to={to}
      className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-6 hover:shadow-gut8-md transition-all hover:-translate-y-0.5 flex flex-col gap-2"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-full bg-primary-subtle text-primary">{icon}</div>
        <div className="font-semibold text-gut8-anthracite">{title}</div>
      </div>
      <p className="text-sm text-text-muted">{description}</p>
    </Link>
  )
}

function errorDetail(e: any): string {
  return e?.response?.data?.detail ?? e?.message ?? String(e)
}

function ExportImportPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingBundle, setPendingBundle] = useState<any | null>(null)
  const [dryRunResult, setDryRunResult] = useState<any | null>(null)
  const [importResult, setImportResult] = useState<any | null>(null)
  const [error, setError] = useState<string>('')
  const [busy, setBusy] = useState(false)

  const onExport = async () => {
    setError('')
    try {
      const bundle = await exportBundle()
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'gutachten-config-bundle.json'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(errorDetail(e))
    }
  }

  const onFileChosen = async (file: File) => {
    setError('')
    setImportResult(null)
    setDryRunResult(null)
    setPendingBundle(null)
    try {
      const text = await file.text()
      const bundle = JSON.parse(text)
      setBusy(true)
      const result = await importBundle(bundle, true)
      setPendingBundle(bundle)
      setDryRunResult(result)
    } catch (e) {
      setError(errorDetail(e))
    } finally {
      setBusy(false)
    }
  }

  const onConfirmImport = async () => {
    if (!pendingBundle) return
    setError('')
    setBusy(true)
    try {
      const result = await importBundle(pendingBundle, false)
      setImportResult(result)
      setDryRunResult(null)
      setPendingBundle(null)
    } catch (e) {
      setError(errorDetail(e))
    } finally {
      setBusy(false)
    }
  }

  const onCancel = () => {
    setPendingBundle(null)
    setDryRunResult(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gut8-anthracite">Config-Export/Import</h2>
      <p className="text-sm text-text-muted">
        Exportiert die aktiven Bausteine (Beschreibungs-Texte, Wertungen, Lagedaten-Layout, KI-Prompt) als eine
        JSON-Datei bzw. importiert eine solche Datei zurück. Der Import wird zunächst als Probelauf geprüft.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 bg-primary text-text-inverse font-semibold text-sm hover:bg-primary-hover"
          onClick={onExport}
        >
          <Download className="w-4 h-4" /> Exportieren
        </button>

        <label className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted cursor-pointer">
          <Upload className="w-4 h-4" /> Datei wählen …
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) onFileChosen(file)
            }}
          />
        </label>

        {busy && <span className="text-sm text-text-muted">läuft …</span>}
      </div>

      {error && <div className="text-sm text-secondary">Fehler: {error}</div>}

      {dryRunResult && (
        <div className="border border-border-gut8-medium rounded-gut8 p-4 space-y-3 bg-gut8-bg-muted">
          <div className="text-sm font-medium text-gut8-anthracite">Probelauf erfolgreich</div>
          <div className="text-sm text-text-muted">
            Würde importieren: {dryRunResult.would_import?.join(', ') || '–'}
          </div>
          <div className="flex gap-2">
            <button
              className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 bg-primary text-text-inverse font-semibold text-sm hover:bg-primary-hover disabled:opacity-40"
              onClick={onConfirmImport}
              disabled={busy}
            >
              Jetzt importieren
            </button>
            <button
              className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted"
              onClick={onCancel}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {importResult && (
        <div className="border border-border-gut8-medium rounded-gut8 p-4 space-y-1 bg-gut8-bg-muted">
          <div className="text-sm font-medium text-gut8-anthracite">Import abgeschlossen</div>
          <div className="text-sm text-text-muted">Importiert: {importResult.imported?.join(', ') || '–'}</div>
          {importResult.skipped?.length > 0 && (
            <div className="text-sm text-text-muted">
              Übersprungen (noch nicht importierbar): {importResult.skipped.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PresenceRow({ label, present, note }: { label: string; present: boolean; note?: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      {present ? (
        <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
      ) : (
        <XCircle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
      )}
      <span className={present ? 'text-gut8-anthracite' : 'text-secondary'}>
        {label}
        {note && <span className="text-text-muted"> — {note}</span>}
      </span>
    </li>
  )
}

function RequirementsPanel() {
  const [requirements, setRequirements] = useState<any | null>(null)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    getRequirements()
      .then(setRequirements)
      .catch((e) => setError(errorDetail(e)))
  }, [])

  const ratingItems = requirements?.rating_config?.indicators ?? []
  const layoutItems = requirements?.report_layout?.indicators ?? []
  const otherSources = requirements?.report_layout?.other_sources ?? []
  const ratingKeys = requirements?.description_config?.rating_keys ?? []

  return (
    <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-6 space-y-6">
      <h2 className="text-lg font-semibold text-gut8-anthracite">Voraussetzungen</h2>
      <p className="text-sm text-text-muted">
        Prüft, ob die von den aktiven Configs referenzierten Indikatoren tatsächlich in der Datenbank vorhanden sind.
      </p>

      {error && <div className="text-sm text-secondary">Fehler: {error}</div>}

      {!requirements && !error && <div className="text-sm text-text-muted">Lädt …</div>}

      {requirements && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gut8-anthracite mb-2">
              Wertungen — Indikatoren ({ratingItems.length})
            </h3>
            {ratingItems.length === 0 ? (
              <div className="text-sm text-text-muted">Keine aktive Rating-Config.</div>
            ) : (
              <ul className="space-y-1 max-h-96 overflow-y-auto pr-2">
                {ratingItems.map((item: any) => (
                  <PresenceRow
                    key={`${item.ref}-${item.by_code}`}
                    label={item.ref}
                    present={item.present}
                    note={item.by_code ? 'per Code' : undefined}
                  />
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gut8-anthracite mb-2">
              Lagedaten-Layout — Indikatoren ({layoutItems.length})
            </h3>
            {layoutItems.length === 0 ? (
              <div className="text-sm text-text-muted">Kein aktives Lagedaten-Layout.</div>
            ) : (
              <ul className="space-y-1 max-h-96 overflow-y-auto pr-2">
                {layoutItems.map((item: any) => (
                  <PresenceRow key={item.ref} label={item.ref} present={item.present} note={item.note} />
                ))}
              </ul>
            )}
            {otherSources.length > 0 && (
              <div className="text-xs text-text-muted mt-2">
                + {otherSources.length} weitere Quellen (Stammdaten/Klassifikation/Leittexte, nicht direkt geprüft)
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-gut8-anthracite mb-2">
              Beschreibungs-Texte — verwendete Rating-Keys ({ratingKeys.length})
            </h3>
            {ratingKeys.length === 0 ? (
              <div className="text-sm text-text-muted">Keine aktive Beschreibungs-Config.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {ratingKeys.map((key: string) => (
                  <span
                    key={key}
                    className="text-xs px-2 py-1 rounded-full bg-gut8-bg-muted border border-border-gut8-light text-gut8-anthracite"
                  >
                    {key}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export function GutachtenDashboard() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gut8-anthracite">Gutachterliche Config</h1>
        <p className="text-text-muted mt-1">
          Zentrale Übersicht für alle Bausteine der KI-gestützten Lagebeschreibung: Prompt, Wertungen, Lagedaten-Layout und Beschreibungs-Texte.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile
          to="/admin/prompts"
          icon={<FileText className="w-5 h-5" />}
          title="KI-Prompt"
          description="Instruktionstext für die KI-Generierung verwalten."
        />
        <Tile
          to="/admin/rating-config"
          icon={<Scale className="w-5 h-5" />}
          title="Wertungen"
          description="Rating-Rezept: Gewichte, Skalen und Aggregationsbaum konfigurieren."
        />
        <Tile
          to="/admin/report-layout"
          icon={<Table className="w-5 h-5" />}
          title="Lagedaten-Layout"
          description="Struktur der Lagedaten-Tabelle und des Datenblocks bearbeiten."
        />
        <Tile
          to="/admin/description-config"
          icon={<MessageSquareText className="w-5 h-5" />}
          title="Beschreibungs-Texte"
          description="Satzbausteine der generierten Lagebeschreibung editieren."
        />
      </div>

      <div id="gutachten-export-import" className="mt-8">
        <ExportImportPanel />
      </div>
      <div id="gutachten-requirements" className="mt-8">
        <RequirementsPanel />
      </div>
    </div>
  )
}
